import type { UnmappedElement } from "../types";
import type { MappingLayoutMode } from "../hooks/useMappingElements";
import { mappingSelectionKey } from "../hooks/useMappingElements";
import type { ComponentParameter } from "../types";

type ColumnSelection = ComponentParameter | null | undefined;
type SelectionMap = Record<string, Record<string, ColumnSelection>>;

/** Human-readable label for an element scope in validation copy. */
export function elementScopeLabel(
  element: UnmappedElement,
  layoutMode: MappingLayoutMode,
  componentNamesByElement: Record<string, string[]>,
): string {
  if (layoutMode === "individual") {
    return element.componentCode;
  }
  const names = componentNamesByElement[element.elementQualifiedName];
  return names && names.length > 0
    ? names.join(", ")
    : element.elementQualifiedName;
}

/**
 * Elements shown on Map Columns that have no CSV column mapped to a parameter.
 * Skipped and unset columns do not count as mapped.
 */
export function getElementsMissingMappings(
  elements: UnmappedElement[],
  dataColumns: string[],
  selections: SelectionMap,
  layoutMode: MappingLayoutMode,
): UnmappedElement[] {
  if (dataColumns.length === 0) {
    return [];
  }

  return elements.filter((element) => {
    const scopeKey = mappingSelectionKey(element, layoutMode);
    const scopeSelections = selections[scopeKey] ?? {};
    return !dataColumns.some((col) => scopeSelections[col] != null);
  });
}

export function formatMissingMappingsMessage(
  elements: UnmappedElement[],
  layoutMode: MappingLayoutMode,
  componentNamesByElement: Record<string, string[]>,
): string {
  const labels = elements.map((el) =>
    elementScopeLabel(el, layoutMode, componentNamesByElement),
  );
  if (labels.length === 1) {
    return `Map at least one CSV column to a parameter for ${labels[0]} before continuing.`;
  }
  return `Map at least one CSV column to a parameter for each component before continuing: ${labels.join(", ")}.`;
}
